import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';

const ConfigContext = createContext();

const DEFAULT_CONFIG = {
    primaryColor: '#c20042',
    secondaryColor: '#810691',
    backgroundColor: '#550043',
    globalFontColor: '#ffffff',
    sidebarBgColor: '#550043',
    sidebarFontColor: '#ffffff',
    logoUrl: '/assets/cardio/CS_Logo.png',
    buttonStyles: { borderRadius: '12px', padding: '16px 24px' },
    pages: {},
    pageDesigns: {},
    event_letters: {},
    registration_fields: {},
    bingo_config: { enabled: false, bonus_points: 50, title: '🎱 BINGO', cardBgColor: '#1e1b4b', cardBgImage: '' },
    encuesta_config: { enabled: true, title: 'ENCUESTA', url: '', type: 'internal' },
    fontFamily: 'Poppins',
    secondaryFontFamily: 'Poppins',
    tertiaryFontFamily: '',
    backgroundImage: '/assets/cardio/CS_Fondo-vertical.png',
    backgroundMobile: '/assets/cardio/CS_Fondo-vertical.png',
    backgroundDesktop: '/assets/cardio/CS_Fondo-Horizontal.png',
    elementShape: 'rounded-xl'
};

export const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const [visualRes, pagesRes, customRes, designsRes, lettersRes, regRes, bingoRes, encuestaRes] = await Promise.all([
                    api.get('/configs/visual_identity').catch(() => ({ data: null })),
                    api.get('/configs/page_controls').catch(() => ({ data: null })),
                    api.get('/configs/custom_gallery_pages').catch(() => ({ data: [] })),
                    api.get('/configs/page_designs').catch(() => ({ data: null })),
                    api.get('/configs/event_letters').catch(() => ({ data: null })),
                    api.get('/configs/registration_fields').catch(() => ({ data: null })),
                    api.get('/configs/bingo_config').catch(() => ({ data: null })),
                    api.get('/configs/encuesta_config').catch(() => ({ data: null })),
                ]);

                const newConfig = {
                    ...(visualRes.data || {}),
                    pages: pagesRes.data || {},
                    customGalleryPages: customRes.data || [],
                    pageDesigns: designsRes.data || {},
                    event_letters: lettersRes.data || {},
                    registration_fields: regRes.data || {},
                    bingo_config: bingoRes.data || DEFAULT_CONFIG.bingo_config,
                    encuesta_config: encuestaRes.data || DEFAULT_CONFIG.encuesta_config,
                };
                
                // Inject fonts to DOM
                if (newConfig.fontFamily) {
                    document.documentElement.style.setProperty('--main-font', `"${newConfig.fontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--main-font', 'inherit');
                }
                if (newConfig.secondaryFontFamily) {
                    document.documentElement.style.setProperty('--secondary-font', `"${newConfig.secondaryFontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--secondary-font', 'inherit');
                }
                if (newConfig.tertiaryFontFamily) {
                    document.documentElement.style.setProperty('--tertiary-font', `"${newConfig.tertiaryFontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--tertiary-font', 'inherit');
                }

                // Inject colors to DOM
                if (newConfig.primaryColor) {
                    document.documentElement.style.setProperty('--primary-purple', newConfig.primaryColor);
                } else {
                    document.documentElement.style.setProperty('--primary-purple', '#9d4edd');
                }
                if (newConfig.backgroundColor) {
                    document.documentElement.style.setProperty('--bg-deep-purple', newConfig.backgroundColor);
                } else {
                    document.documentElement.style.setProperty('--bg-deep-purple', '#1c0042');
                }
                if (newConfig.globalFontColor) {
                    document.documentElement.style.setProperty('--global-font-color', newConfig.globalFontColor);
                } else {
                    document.documentElement.style.setProperty('--global-font-color', '#000000');
                }
                // Sidebar
                document.documentElement.style.setProperty('--sidebar-bg', newConfig.sidebarBgColor || '#7f1d1d');
                document.documentElement.style.setProperty('--sidebar-font', newConfig.sidebarFontColor || '#ffffff');

                setConfig(prev => JSON.stringify(prev) === JSON.stringify(newConfig) ? prev : newConfig);
            } catch (error) {
                console.error("Config fetch failed, using defaults", error);
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
        // Polling cada 30 segundos en vez de cada 2 segundos para no saturar sockets ni CPU
        const interval = setInterval(fetchConfig, 30000);
        return () => clearInterval(interval);
    }, []);

    return (
        <ConfigContext.Provider value={{ config, loading }}>
            {children}
        </ConfigContext.Provider>
    );
};

export const useConfig = () => useContext(ConfigContext);
